import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { asPercent } from '../../../helpers';

import PerfectScrollbar from 'react-perfect-scrollbar';
import { useTheme } from '@emotion/react';
import { IconButton, TableContainer, Button, TextField } from '@mui/material';
import { 
  Delete, 
  Close, 
  Add, 
  RecentActors, 
  Search, 
  PanoramaFishEye, 
  CheckCircleOutline, 
  HighlightOff, 
  FirstPage, 
  KeyboardArrowLeft, 
  KeyboardArrowRight, 
  LastPage
} from '@mui/icons-material';
import {
  CardActions,
  CardContent,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import useClasses from '../../../helpers/useClasses';


const styles = theme => ({
  root: {},
  content: {
    padding: 0
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  searchInput: {
    marginLeft: theme.spacing(1),
    flex: 1,
    width: '40%'
  },
  membership_status: {
    justifyContent: 'flex-end'
  },
  membership_trialing: {
    color: '#fb8c00',
  },
  membership_active: {
    color: '#43a047'
  },
  membership_inactive: {
    color: '#C22121'
  },
  contribution_container: {
    display: 'flex'
  },
  contribution_caution: {
    color: '#fb8c00',
    fontWeight: 'bold'
  },
  contribution_ok: {
    color: '#43a047',
    fontWeight: 'bold'
  },
  contribution_low: {
    color: '#C22121',
    fontWeight: 'bold'
  },
  contribution_indicator_override: {
    backgroundColor: '#43a047',
    padding: '6px',
    borderRadius: '20px',
    margin: '5px 0 5px 20px'
  },
  contribution_indicator_locked: {
    backgroundColor: '#C22121',
    padding: '6px',
    borderRadius: '20px',
    margin: '5px 0 5px 20px'
  },
  contribution_indicator_warning: {
    backgroundColor: '#fb8c00',
    padding: '6px',
    borderRadius: '20px',
    margin: '5px 0 5px 20px'
  },
  chip: {
    verticalAlign: 'top',
    marginLeft: '10px'
  },
  filter_label: {
    display: 'inline-block',
    padding: '13px 5px'
  }

});

const AccountsTable = props => {
  const { 
    accounts,
    onAccountApiAccessToggle,
    onArchiveClick,
    pagination,
    onPageChange,
    onPerPageChange,
    onSearchClick,
    onSearchCancelClick,
    currentSearch,
    onMembershipFilterChange,
    onPlanLevelFilterChange,
    isFiltered,
    currentMembershipFilter,
    currentPlanLevelFilter,
    onAccountMfaRequiredToggle,
    onExportContactsClick,
  } = props;
  
  const classes = useClasses(styles);

  const [search, setSearch] = useState(currentSearch)
  const [membership_filter, setMembershipFilter] = useState(currentMembershipFilter)
  const [plan_level_filter, setPlanLevelFilter] = useState(currentPlanLevelFilter)
  
  const handleSearchChange = event => {
    setSearch(event.target.value)
  }

  const handleMembershipFilterChange = event => {
    setMembershipFilter(event.target.value)
    onMembershipFilterChange(event, event.target.value)
  }

  const handlePlanLevelFilterChange = event => {
    setPlanLevelFilter(event.target.value)
    onPlanLevelFilterChange(event, event.target.value)
  }

  const handleExportContactsClick = event => {
    onExportContactsClick(event)
  }

  const styles2 = theme => ({
    root: {
      flexShrink: 0,
      marginLeft: theme.spacing(2.5),
    },
  });

  const membership_filter_options = [
    {
      label: 'Active Membership',
      value: 'active'
    },
    {
      label: 'Inactive/Deleted',
      value: 'canceled'
    },
    {
      label: 'All',
      value: ''
    }
  ];

  const plan_level_filter_options = [
    {
      label: 'BASIC',
      value: 'BASIC'
    },
    {
      label: 'PRO',
      value: 'PRO'
    },
    {
      label: 'Any',
      value: ''
    }
  ];
  
  const TablePaginationActions = (props) => {
    const classes = useClasses(styles2);
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;
  
    const handleFirstPageButtonClick = event => {
      onPageChange(event, 0);
    };
  
    const handleBackButtonClick = event => {
      onPageChange(event, page - 1);
    };
  
    const handleNextButtonClick = event => {
      onPageChange(event, page + 1);
    };
  
    const handleLastPageButtonClick = event => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
      <div className={classes.root}>
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={page === 0}
          aria-label="first page"
        >
          {theme.direction === 'rtl' ? <LastPage /> : <FirstPage />}
        </IconButton>
        <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
          {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          {theme.direction === 'rtl' ? <FirstPage /> : <LastPage />}
        </IconButton>
      </div>
    );
  }

  const ContributionPercentage = (props) => {
    const { val, locked, warning, override } = props;
    const percent = asPercent(val);
    let cssName = classes.contribution_caution;

    if (percent > 1)
      cssName = classes.contribution_ok;
    if (percent < 0.5)
      cssName = classes.contribution_low;

    return (
      <div className={cssName}>
        { percent } % 
      </div>
    );
  }

  const ContributionPercentageIndicator = (props) => {
    const { locked, warning, override } = props;
    
    if (override) {
      return (
        <div className={classes.contribution_indicator_override}></div>
      )
    }

    if (locked && !override) {
      return (
        <div className={classes.contribution_indicator_locked}></div>
      )
    }

    if (warning && !locked && !override) {
      return (
        <div className={classes.contribution_indicator_warning}></div>
      )
    }

    return (
      null
    );
  }


  const PlanLevelPill = (props) => {
    const { plan_level } = props;
    if (plan_level === "BASIC"){
      return (
        <Chip label="BASIC" size="small" variant="outline" sx={{
          verticalAlign: 'top',
          marginLeft: '10px'
        }} />
      );
    } else {
      return (
        <Chip label="PRO" size="small" color="primary" sx={{
          verticalAlign: 'top',
          marginLeft: '10px'
        }} />
      );
    }
  }




  return (
    <React.Fragment>
      <CardContent sx={{
          padding: 0
        }}>
        <PerfectScrollbar>
          <TableContainer className={classes.inner}>
            <Table>
              <TableHead sx={{
                backgroundColor: "#fafafa"
              }}>
                <TableRow>
                  <TableCell colSpan={2}>
                    <TextField
                      // className={classes.searchInput}
                      sx={{
                        // marginLeft: '10px',
                        flex: 1,
                        width: '40%'
                      }}
                      placeholder="Search by Funder name"
                      inputProps={{ 'aria-label': 'Search by Funder name' }}
                      value={search}
                      variant='standard'
                      onChange={(event) => handleSearchChange(event)}
                    />
                    <IconButton
                      type="submit"
                      // className={classes.iconButton}
                      sx={{
                        padding: '10px',
                      }}
                      onClick={(event) => onSearchClick(event, search)}
                      aria-label="search">
                      <Search />
                    </IconButton>
                    { isFiltered ?
                      <IconButton
                        type="submit"
                        // className={classes.iconButton}
                        sx={{
                          padding: '10px',
                        }}
                        onClick={(event) => onSearchCancelClick(event, search)}
                        aria-label="clear search">
                        <Close />
                      </IconButton>
                    : null }
                  </TableCell>
                  <TableCell colSpan={2}>
                    {/* <span className={classes.filter_label}>Membership Status</span> */}
                    <FormControl sx={{ m: 1, minWidth: 200 }} size="small">
                      {/* <InputLabel id="membership-status-label">Membership&nbsp;Status</InputLabel> */}
                      <TextField
                        // className={classes.searchInput}
                        sx={{
                          // marginLeft: '10px',
                          flex: 1,
                          // width: '40%',
                          padding: '0',
                          margin: '0'
                        }}
                        label="Membership Status"
                        margin="dense"
                        name="membership_filter"
                        value={membership_filter || ''}
                        onChange={handleMembershipFilterChange}
                        select
                        // SelectProps={{ native: true }}
                        // variant="outlined"
                      >
                        {membership_filter_options.map(option => (
                          // <option
                          //   key={option.value}
                          //   value={option.value}
                          // >
                          //   {option.label}
                          // </option>
                          <MenuItem
                            value={option.value}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      {/* <Select
                        name="membership_filter"
                        value={membership_filter || ''}
                        label="Membership Status"
                        // labelId="membership-status-label"
                        // sx={{
                        //   // marginLeft: '10px',
                        //   flex: 1,
                        //   width: '40%',
                        //   padding: '0'
                        // }}
                        variant="outlined"
                        // margin="dense"
                        onChange={handleMembershipFilterChange}
                      >
                        {membership_filter_options.map(option => (
                          <MenuItem
                            value={option.value}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select> */}
                    </FormControl>
                  </TableCell>
                  <TableCell colSpan={2}>
                  <FormControl sx={{ m: 1, minWidth: 150 }} size="small">
                      {/* <InputLabel id="plan-level-label">Plan&nbsp;Level</InputLabel> */}
                      <TextField
                        // className={classes.searchInput}
                        sx={{
                          // marginLeft: '10px',
                          flex: 1,
                          // width: '40%',
                          padding: '0',
                          margin: '0'
                        }}
                        label="Plan Level"
                        margin="dense"
                        name="plan_level_filter"
                        value={plan_level_filter || ''}
                        onChange={handlePlanLevelFilterChange}
                        select
                        // SelectProps={{ native: true }}
                        // variant="outlined"
                      >
                        {plan_level_filter_options.map(option => (
                          // <option
                          //   key={option.value}
                          //   value={option.value}
                          // >
                          //   {option.label}
                          // </option>
                          <MenuItem
                            value={option.value}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      {/* <Select
                        name="plan_level_filter"
                        value={plan_level_filter || ''}
                        label=""
                        labelId="plan-level-label"
                        // sx={{
                        //   // marginLeft: '10px',
                        //   flex: 1,
                        //   width: '40%',
                        //   padding: '0'
                        // }}
                        onChange={handlePlanLevelFilterChange}
                      >
                        {plan_level_filter_options.map(option => (
                          <MenuItem
                            value={option.value}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select> */}
                    </FormControl>
                    {/* <span className={classes.filter_label}>Plan Level</span>
                    <TextField
                      // className={classes.searchInput}
                      sx={{
                        // marginLeft: '10px',
                        flex: 1,
                        width: '40%'
                      }}
                      label=""
                      margin="dense"
                      name="plan_level_filter"
                      value={plan_level_filter || ''}
                      onChange={handlePlanLevelFilterChange}
                      select
                      SelectProps={{ native: true }}
                      variant="outlined"
                    >
                      {plan_level_filter_options.map(option => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </TextField> */}
                  </TableCell>
                  <TableCell colSpan={1} align={'right'}>
                    <Button
                      variant="contained"
                      className={classes.button}
                      startIcon={<RecentActors />}
                      onClick={handleExportContactsClick}
                    >
                      Export Contacts
                    </Button>
                  </TableCell>
                  <TableCell colSpan={1} align={'right'}>
                    <Link to="/funders/new">
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        startIcon={<Add />}
                      >
                        Add Funder
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableHead sx={{
                backgroundColor: "#fafafa"
              }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Merchants Added</TableCell>
                  <TableCell>Last Added</TableCell>
                  <TableCell>90 Day Cont. %</TableCell>
                  <TableCell>API Access</TableCell>
                  <TableCell>MFA Required</TableCell>
                  <TableCell>Membership?</TableCell>
                  <TableCell>Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map(account => (
                  <TableRow
                    className={classes.tableRow}
                    hover
                    key={account.id}
                  >
                    <TableCell>
                      <div className={classes.nameContainer}>
                        <Typography variant="body1">
                          <Link to={`/funders/${account.resource_id}`}>{account.name}</Link>
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell>
                      {account.merchants_added}
                    </TableCell>
                    <TableCell>
                      {moment(account.last_merchant_added_at).format('MM/DD/YYYY')}
                    </TableCell>
                    <TableCell>
                      <div className={classes.contribution_container}>
                        <ContributionPercentage
                          component="div"
                          val = { account.total_ninety_days_contribution_percentage }
                          locked = { account.contribution_locked }
                          warning = { account.contribution_warning_date }
                          override = { account.contribution_override }
                        />
                        <ContributionPercentageIndicator
                          component="div"
                          locked = { account.contribution_locked }
                          warning = { account.contribution_warning_date }
                          override = { account.contribution_override }
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={account.can_access_api}
                        color="primary"
                        onChange={event => onAccountApiAccessToggle(event, account)}
                        value="true"
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={account ? account.mfa_required : false}
                        color="primary"
                        onChange={event => onAccountMfaRequiredToggle(event, account)}
                        value="true"
                      />
                    </TableCell>
                    <TableCell
                      // className={classes.membership_status}
                      sx={{
                        justifyContent: 'flex-end'
                      }}
                    >
                      {account.membership_status ? 
                        account.membership_status === 'trialing' ? <PanoramaFishEye sx={{
                          color: '#fb8c00',
                        }} /> : <span><CheckCircleOutline sx={{
                          color: '#43a047'
                        }} /><PlanLevelPill plan_level={account.plan_level} /></span>
                        : <HighlightOff sx={{
                          color: '#C22121'
                        }} />
                      }
                    </TableCell>
                    <TableCell>
                      <IconButton aria-label="delete" onClick={event => onArchiveClick(event, account.resource_id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </PerfectScrollbar>
      </CardContent>
      <CardActions
        // className={classes.actions}
        sx={{
          justifyContent: 'flex-end'
        }}
      >
        <TablePagination
          component="div"
          count={ pagination ? pagination.total_count : 0}
          onPageChange={onPageChange}
          onChangeRowsPerPage={onPerPageChange}
          page={pagination ? pagination.current_page : 0}
          rowsPerPage={pagination ? pagination.per_page : 25}
          rowsPerPageOptions={[5, 10, 25]}
          ActionsComponent={TablePaginationActions}
        />
      </CardActions>
    </React.Fragment>
  );
};

export default AccountsTable;
